import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Plus } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import Filters from "../../../../components/Filter";
import DateRangeFilterInput from "../../../../components/Filter/DateRangeFilterInput";
import AddReportModal from "../../../../components/Events/Event/Reports/AddReportModal";
import {
  buildIssueImagesAttributes,
} from "../../../../components/Events/Event/Reports/reportUpload";
import {
  UIButton,
  UIExportModal,
  UIIcon,
  UIPaginationBar,
} from "../../../../components/ui";
import ReportsEmptyState from "../../../../components/Events/Event/Reports/ReportsEmptyState";
import ReportsTable from "../../../../components/Events/Event/Reports/ReportsTable";
import useDebounce from "../../../../hooks/useDebounce";
import messages from "../../../../assets/locale/messages";
import { companyPermissionModules } from "../../../../utils/Constants";
import { canAddCompanyModule } from "../../../../utils/Permissions";
import {
  EVENT_REPORT_URL_PARAM,
  EVENT_VIEW_TABS,
} from "../../../../utils/eventViewTabs";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  createCompanyLogisticsIssueRequest,
  exportCompanyLogisticsIssuesRequest,
  getCompanyLogisticsIssuesRequest,
} from "../../../../store/CompanyPortal/LogisticsIssues/actions";
import {
  buildCompanyReportsListParams,
  buildReportExportParams,
  EMPTY_REPORT_EXPORT_FIELDS,
  getReporterPositionFilterOptions,
  getReportExportFields,
  joinSelectedReportExportColumns,
  mapReportRow,
} from "../../../../components/Events/Event/Reports/reportMappers";

import "../../Reports/Reports.scss";

const SEARCH_DEBOUNCE_MS = 300;

const CompanyEventReportsTab = ({ event }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const list = useSelector((state) => state.companyLogisticsIssues.list);
  const meta = useSelector((state) => state.companyLogisticsIssues.meta);
  const isLoading = useSelector(
    (state) => state.companyLogisticsIssues.isLoading,
  );
  const creating = useSelector((state) => state.companyLogisticsIssues.creating);
  const { report, shared } = messages[lang];

  const canAdd = canAddCompanyModule(
    companyPermissionModules.reports,
    login,
  );

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounce(searchValue, SEARCH_DEBOUNCE_MS);
  const [zoneFilter, setZoneFilter] = useState(null);
  const [reporterPositionFilter, setReporterPositionFilter] = useState(null);
  const [reportedAtFilter, setReportedAtFilter] = useState(null);
  const [resolvedAtFilter, setResolvedAtFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedFields, setSelectedFields] = useState(EMPTY_REPORT_EXPORT_FIELDS);

  const eventId = event?.id;

  const zoneOptions = useMemo(() => {
    const zones = event?.zones_attributes || event?.zones || [];
    return (Array.isArray(zones) ? zones : []).map((zone) => ({
      id: zone.id,
      name: zone.name || zone.attributes?.name || String(zone.id),
    }));
  }, [event]);

  const reporterPositionOptions = useMemo(
    () => getReporterPositionFilterOptions(report.reporterPositions),
    [report.reporterPositions],
  );

  const listParams = useMemo(
    () =>
      buildCompanyReportsListParams({
        search: debouncedSearch,
        page,
        pageSize,
        eventId,
        zoneId: zoneFilter?.id,
        issuerType: reporterPositionFilter?.id,
        reportedAt: reportedAtFilter,
        resolvedAt: resolvedAtFilter,
      }),
    [
      debouncedSearch,
      page,
      pageSize,
      eventId,
      zoneFilter,
      reporterPositionFilter,
      reportedAtFilter,
      resolvedAtFilter,
    ],
  );

  useEffect(() => {
    if (!eventId) return;
    dispatch(getCompanyLogisticsIssuesRequest(listParams));
  }, [dispatch, eventId, listParams, lang]);

  const hasActiveFilters = Boolean(
    debouncedSearch.trim() ||
      zoneFilter ||
      reporterPositionFilter ||
      reportedAtFilter ||
      resolvedAtFilter,
  );

  const rows = useMemo(
    () =>
      (list || []).map((issue) => mapReportRow(issue, report.reporterPositions)),
    [list, report.reporterPositions],
  );
  const totalCount = meta.count || 0;
  const paginationPages =
    meta.pages || Math.max(Math.ceil(totalCount / pageSize), 1);
  const paginationFrom = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const paginationTo = Math.min(page * pageSize, totalCount);

  const addModalLabels = useMemo(
    () => ({
      title: report.addReport.title,
      description: report.addReport.description,
      cancel: shared.cancel,
      submit: report.addReport.submit,
      reportTitle: report.addReport.reportTitle,
      reportTitlePlaceholder: report.addReport.reportTitlePlaceholder,
      zone: report.addReport.zone,
      zonePlaceholder: report.addReport.zonePlaceholder,
      issueDescription: report.addReport.issueDescription,
      issueDescriptionPlaceholder: report.addReport.issueDescriptionPlaceholder,
      descriptionHint: report.addReport.descriptionHint,
      descriptionMaxLengthError: report.addReport.descriptionMaxLengthError,
      open: report.listReport.open,
      closed: report.listReport.closed,
      reportStatus: report.addReport.reportStatus,
      uploadLabel: report.addReport.uploadLabel,
      uploadClickToUpload: report.addReport.uploadClickToUpload,
      uploadDragHint: report.addReport.uploadDragHint,
      uploadHint: report.addReport.uploadHint,
      uploadFormatHint: report.addReport.uploadFormatHint,
      uploadRemainingHint: report.addReport.uploadRemainingHint,
      uploadLimitReached: report.addReport.uploadLimitReached,
      uploadMaxSizeError: report.addReport.uploadMaxSizeError,
      uploadTypeError: report.addReport.uploadTypeError,
      required: report.addReport.required,
    }),
    [report, shared.cancel],
  );

  const tableLabels = useMemo(
    () => ({
      reportTitle: report.listReport.reportTitle,
      zone: report.listReport.zones,
      name: report.listReport.name,
      role: report.listReport.role,
      createdDateTime: report.listReport.createdDateTime,
      closedDateTime: report.listReport.closedDateTime,
      dateTime: report.listReport.dateTime,
      status: report.listReport.status,
      open: report.listReport.open,
      closed: report.listReport.closed,
    }),
    [report.listReport],
  );

  const exportFields = useMemo(
    () => getReportExportFields(report.exportSheetColumns, report.updates),
    [report.exportSheetColumns, report.updates],
  );

  const selectedExportCount = Object.values(selectedFields).filter(Boolean)
    .length;
  const isAllExportSelected =
    exportFields.length > 0 && selectedExportCount === exportFields.length;
  const isExportIndeterminate =
    selectedExportCount > 0 && selectedExportCount < exportFields.length;

  const handleCloseExportModal = () => {
    setShowExportModal(false);
    setSelectedFields(EMPTY_REPORT_EXPORT_FIELDS);
  };

  const handleExportSheet = () => {
    if (!eventId) return;

    const columns = joinSelectedReportExportColumns(
      exportFields,
      selectedFields,
    );
    if (!columns) return;

    dispatch(
      exportCompanyLogisticsIssuesRequest({
        params: buildReportExportParams({
          columns,
          listParams,
          includeEventId: true,
        }),
      }),
    );
    handleCloseExportModal();
  };

  const handleCreateReport = useCallback(
    async (formValues) => {
      if (!eventId) return;
      try {
        const imageUrls =
          formValues.imageUrls ||
          (formValues.imageUrl ? [formValues.imageUrl] : []);
        const data = {
          event_id: eventId,
          zone_id: formValues.zoneId,
          title: formValues.title,
          description: formValues.description,
          status: formValues.status,
        };
        const images = buildIssueImagesAttributes(imageUrls);
        if (images.length) {
          data.logistics_issue_images_attributes = images;
        }

        dispatch(
          createCompanyLogisticsIssueRequest({
            data,
            onSuccess: () => {
              setShowAddModal(false);
              dispatch(getCompanyLogisticsIssuesRequest(listParams));
            },
          }),
        );
      } catch (error) {
        // saga handle messaging
      }
    },
    [dispatch, eventId, listParams],
  );

  if (!eventId) return null;

  return (
    <div className="company-reports company-reports--event-tab">
      <Filters
        isShowSearch
        isCollapsable
        renderActionBeforeFilterToggle={
          <>
            <UIButton
              type="button"
              variant="primary"
              appearance="outline"
              size="large"
              startIcon={
                <UIIcon
                  name="exportSheet"
                  width={18}
                  height={18}
                  color="currentColor"
                  strokeWidth={1.5}
                  decorative
                />
              }
              onClick={() => setShowExportModal(true)}
            >
              {report.exportSheetColumns.exportReportToExcel}
            </UIButton>
            {canAdd ? (
              <UIButton
                type="button"
                variant="primary"
                appearance="outline"
                size="large"
                startIcon={<Plus size={16} aria-hidden />}
                onClick={() => setShowAddModal(true)}
              >
                {report.addReport.addNewReport}
              </UIButton>
            ) : null}
          </>
        }
        searchInput={{
          placeholder: report.searchPlaceholder,
          name: "eventReportSearch",
          value: searchValue,
          onChange: (value) => {
            setSearchValue(value);
            setPage(1);
          },
        }}
        filters={[
          {
            type: "filter",
            name: "zone",
            placeholder: report.listReport.zone,
            value: zoneFilter,
            options: zoneOptions,
            onChange: (value) => {
              setZoneFilter(value);
              setPage(1);
            },
          },
          {
            type: "filter",
            name: "reporterPosition",
            placeholder: report.reporterPosition,
            value: reporterPositionFilter,
            options: reporterPositionOptions,
            onChange: (value) => {
              setReporterPositionFilter(value);
              setPage(1);
            },
          },
          {
            type: "component",
            name: "reportedAt",
            className: "col-sm-12 col-md-6 col-lg-3",
            render: () => (
              <DateRangeFilterInput
                name="reportedAt"
                placeholder={report.reportedAt}
                labels={report.dateRangeFilter}
                value={reportedAtFilter}
                onChange={(value) => {
                  setReportedAtFilter(value);
                  setPage(1);
                }}
              />
            ),
          },
          {
            type: "component",
            name: "resolvedAt",
            className: "col-sm-12 col-md-6 col-lg-3",
            render: () => (
              <DateRangeFilterInput
                name="resolvedAt"
                placeholder={report.resolvedAt}
                labels={report.dateRangeFilter}
                value={resolvedAtFilter}
                onChange={(value) => {
                  setResolvedAtFilter(value);
                  setPage(1);
                }}
              />
            ),
          },
        ]}
        classNames={{ container: "reports-list-filters" }}
      />

      {isLoading ? null : !rows.length ? (
        <ReportsEmptyState
          title={hasActiveFilters ? report.noResultsTitle : report.emptyTitle}
          description={
            hasActiveFilters
              ? report.noResultsDescription
              : report.eventEmptyDescription
          }
          actionLabel={report.addReport.addNewReport}
          onAction={() => setShowAddModal(true)}
          showAction={!hasActiveFilters && canAdd}
        />
      ) : (
        <>
          <ReportsTable
            rows={rows}
            labels={tableLabels}
            pageSize={pageSize}
            variant="event"
            onRowClick={(row) => {
              if (!row?.id || !eventId) return;
              const params = new URLSearchParams(location.search);
              params.set("tab", EVENT_VIEW_TABS.reports);
              params.set(EVENT_REPORT_URL_PARAM, String(row.id));
              history.push({
                pathname:
                  location.pathname ||
                  ROUTE_PATHS.companyViewEvent.replace(":id", eventId),
                search: params.toString(),
              });
            }}
          />
          {totalCount > 0 ? (
            <UIPaginationBar
              currentPage={page}
              pages={paginationPages}
              pageSize={pageSize}
              rangeTemplate={report.showingRange}
              rangeFrom={paginationFrom}
              rangeTo={paginationTo}
              rangeTotal={totalCount}
              rowsPerPageLabel={shared.numberOfRows}
              onPageChange={setPage}
              onPageSizeChange={(nextSize) => {
                setPageSize(nextSize);
                setPage(1);
              }}
              className="reports-list-pagination"
            />
          ) : null}
        </>
      )}

      <UIExportModal
        open={showExportModal}
        title={report.modalTitle}
        description={report.exportModalDescription}
        selectAllLabel={report.selectAllFields}
        cancelLabel={shared.cancel}
        confirmLabel={report.exportSheetColumns.export}
        fields={exportFields}
        selectedFields={selectedFields}
        isAllSelected={isAllExportSelected}
        isIndeterminate={isExportIndeterminate}
        confirmDisabled={selectedExportCount === 0}
        onClose={handleCloseExportModal}
        onCancel={handleCloseExportModal}
        onConfirm={handleExportSheet}
        onFieldChange={(name, checked) =>
          setSelectedFields((prev) => ({ ...prev, [name]: checked }))
        }
        onSelectAll={(checked) =>
          setSelectedFields((prev) => {
            const next = { ...prev };
            exportFields.forEach((field) => {
              next[field.name] = checked;
            });
            return next;
          })
        }
      />

      <AddReportModal
        open={showAddModal}
        labels={addModalLabels}
        zoneOptions={zoneOptions}
        submitting={creating}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleCreateReport}
      />
    </div>
  );
};

CompanyEventReportsTab.propTypes = {
  event: PropTypes.object,
};

CompanyEventReportsTab.defaultProps = {
  event: null,
};

export default CompanyEventReportsTab;
